// prod_mvt_crates operations are namespaced as 'family__subtype'
// ('forklift__receivedDirty', 'special__maintenance'), and for several families the
// bare family name is itself a stored operation. Anything reasoning about a whole
// family therefore has to compare the prefix instead of the operation.

import { rootStatusOf } from '@/helpers/crateTypes'

export const SPECIAL = 'special'

// Maintenance and cleaning come around often enough to deserve their own operation
// rather than being told apart by the comment an operator retyped every day.
export const SPECIAL_MAINTENANCE = 'special__maintenance'
export const SPECIAL_CLEANING = 'special__cleaning'

// Client-specific crate types are ordinary crates that went through a stricter
// preparation process for a demanding client. Unlike every other special movement --
// which is stock-neutral, `TYPE__SPECIAL` on both ends -- these two deliberately move
// stock ACROSS types: preparation takes crates out of the root type's stock and puts
// them in the client type's clean stock, and declassification sends a soiled or
// damaged one back to the ordinary pool. Which of the root's stocks is the type's own
// business -- see rootStatusOf.
export const SPECIAL_CLIENT_WASH = 'special__clientWash'
export const SPECIAL_CLIENT_DECLASSIFY = 'special__clientDeclassify'

export function operationFamily (operation) {
  return String(operation || '').split('__')[0]
}

export function isSpecialOperation (operation) {
  return operationFamily(operation) === SPECIAL
}

// A client type's crates are already washed and repaired, so nothing may be declared
// against one but preparation, declassification, and moving the finished crates.
// Mirrors allowedOperationsForType in the API's helpers/crateTypes.js.
const CLIENT_TYPE_OPERATIONS = [
  SPECIAL_CLIENT_WASH,
  SPECIAL_CLIENT_DECLASSIFY,
  'forklift__receivedClean',
  'forklift__deliveredClean'
]

// null means "every operation", which is what a universal type allows.
export function allowedOperationsForType (descriptor) {
  if (descriptor && descriptor.kind === 'client') return CLIENT_TYPE_OPERATIONS
  return null
}

export function operationAllowedForType (operation, descriptor) {
  const allowed = allowedOperationsForType(descriptor)
  return !allowed || allowed.includes(operation)
}

// The status strings one declared line writes. Preparation and declassification
// straddle two types, so the pair cannot be derived from the selected type alone --
// and a double-washed type draws on the root's clean stock rather than its dirty one,
// so the root's status cannot be assumed either.
export function specialMovementStatuses (operation, crateType, descriptor) {
  const root = descriptor && descriptor.root
  const rootCell = `${root}__${rootStatusOf(descriptor)}`
  if (operation === SPECIAL_CLIENT_WASH) {
    return { start: rootCell, end: `${crateType}__CLEAN` }
  }
  if (operation === SPECIAL_CLIENT_DECLASSIFY) {
    return { start: `${crateType}__CLEAN`, end: rootCell }
  }
  // Every other special activity is an hours record against the type, not a move.
  return { start: `${crateType}__SPECIAL`, end: `${crateType}__SPECIAL` }
}
